import React, { useState, useEffect } from "react";
import { 
  User, 
  Bell, 
  Activity, 
  Shield, 
  Save, 
  RefreshCcw,
  Clock,
  Lock,
  Heart,
  Stethoscope
} from "lucide-react";
import DoctorSidebar from "./DoctorSidebar";
import SecurityModal from "../../components/SecurityModal";
import { useAuth } from "../../context/AuthContext";
import { useToast } from "../../context/ToastContext";
import { db } from "../../firebase/config";
import { ref, update } from "firebase/database";
import "./doctor.css";

const DoctorSettings = () => {
  const { currentUser, userData } = useAuth();
  const { showAlert } = useToast();
  const [profile, setProfile] = useState({
    fullName: "",
    specialization: "",
    email: "",
    license: "",
    department: "",
    education: "",
    consultationFee: 500,
    experienceYears: 0,
    languages: ["English"],
    availableDays: ["Mon", "Tue", "Wed", "Thu", "Fri"],
    availableTime: "09:00-17:00"
  });

  const [loading, setLoading] = useState(false);
  const [showSecurityModal, setShowSecurityModal] = useState(false);

  useEffect(() => {
    if (userData) {
      setProfile({
        fullName: userData.name || "",
        specialization: userData.specialty || userData.specialization || "General Physician",
        email: userData.email || "",
        license: userData.license || "PENDING",
        department: userData.department || "General Medicine",
        education: userData.education || "MBBS",
        consultationFee: userData.consultationFee || 500,
        experienceYears: userData.experienceYears || 0,
        languages: userData.languages || ["English"],
        availableDays: userData.availableDays || ["Mon", "Tue", "Wed", "Thu", "Fri"],
        availableTime: userData.availableTime || "09:00-17:00"
      });
    }
  }, [userData]);

  const [alerts, setAlerts] = useState({
    critical: true,
    warning: true,
    offline: true,
    dailySummary: false
  });

  const [monitoring, setMonitoring] = useState({
    autoRefresh: true,
    interval: "Every 15 seconds"
  });

  const toggleAlert = (key) => {
    setAlerts(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const handleSaveProfile = async () => {
    if (!currentUser) return;
    setLoading(true);
    try {
      const userRef = ref(db, `users/${currentUser.uid}`);
      await update(userRef, {
        name: profile.fullName,
        specialization: profile.specialization,
        specialty: profile.specialization, // Dual-sync for schema flexibility
        email: profile.email,
        license: profile.license,
        department: profile.department,
        education: profile.education,
        role: userData.role || "doctor",
        status: userData.status || "pending",
        emailVerified: userData.emailVerified ?? true,
        consultationFee: Number(profile.consultationFee),
        experienceYears: Number(profile.experienceYears),
        languages: profile.languages,
        availableDays: profile.availableDays,
        availableTime: profile.availableTime
      });
      showAlert("Doctor profile updated successfully!", "success");
    } catch (error) {
      showAlert("Error: " + error.message, "error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="dashboard-shell doctor-portal">
      <DoctorSidebar />

      <main className="dashboard-content">
        <header className="patients-header-main">
          <div className="header-text-main">
            <div className="title-row">
              <Stethoscope size={28} className="icon-blue" />
              <h1>Doctor Settings</h1>
            </div>
            <p>Manage your profile and monitoring preferences</p>
          </div>
        </header>

        <div className="scrollable-content settings-grid-container">
          <div className="settings-grid">
            {/* Clinical Identity Card */}
            <section className="settings-card glass-card clinical-identity-card">
              <div className="card-header">
                <Stethoscope size={20} className="icon-blue" />
                <h3>Clinical Identity & Credentials</h3>
              </div>
              <div className="card-body">
                <div className="settings-form-grid">
                  {/* Basic Info */}
                  <div className="form-group">
                    <label>Full Name</label>
                    <input 
                      type="text" 
                      value={profile.fullName} 
                      onChange={(e) => setProfile({...profile, fullName: e.target.value})}
                    />
                  </div>
                  <div className="form-group">
                    <label>Professional Email</label>
                    <input 
                      type="email" 
                      value={profile.email} 
                      onChange={(e) => setProfile({...profile, email: e.target.value})}
                    />
                  </div>

                  {/* Credentials */}
                  <div className="form-group">
                    <label>Medical Specialization</label>
                    <input 
                      type="text" 
                      value={profile.specialization} 
                      onChange={(e) => setProfile({...profile, specialization: e.target.value})}
                    />
                  </div>
                  <div className="form-group">
                    <label>Department</label>
                    <input 
                      type="text" 
                      value={profile.department} 
                      onChange={(e) => setProfile({...profile, department: e.target.value})}
                    />
                  </div>
                  <div className="form-group">
                    <label>Medical License No.</label>
                    <input 
                      type="text" 
                      value={profile.license} 
                      onChange={(e) => setProfile({...profile, license: e.target.value})}
                    />
                  </div>
                  <div className="form-group">
                    <label>Education / Degree</label>
                    <input 
                      type="text" 
                      value={profile.education} 
                      onChange={(e) => setProfile({...profile, education: e.target.value})}
                    />
                  </div>

                  {/* Practice Stats */}
                  <div className="form-group">
                    <label>Experience (Years)</label>
                    <input 
                      type="number" 
                      value={profile.experienceYears} 
                      onChange={(e) => setProfile({...profile, experienceYears: e.target.value})}
                    />
                  </div>
                  <div className="form-group">
                    <label>Consultation Fee (₹)</label>
                    <input 
                      type="number" 
                      value={profile.consultationFee} 
                      onChange={(e) => setProfile({...profile, consultationFee: e.target.value})}
                    />
                  </div>
                </div>

                <div className="form-group full-width mt-4">
                  <label>Languages (Comma separated)</label>
                  <input 
                    type="text" 
                    value={profile.languages.join(", ")} 
                    onChange={(e) => setProfile({...profile, languages: e.target.value.split(",").map(lang => lang.trim())})}
                  />
                </div>

                <div className="form-group full-width mt-4">
                  <label>Clinical Availability (Time Range)</label>
                  <div className="time-input-wrapper">
                    <Clock size={18} className="icon-blue" />
                    <input 
                      type="text" 
                      placeholder="e.g. 09:00-17:00"
                      value={profile.availableTime} 
                      onChange={(e) => setProfile({...profile, availableTime: e.target.value})}
                    />
                  </div>
                </div>

                <div className="form-group full-width mt-4">
                  <label>Active Consultation Days</label>
                  <div className="day-toggles-hud">
                    {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map(day => (
                      <button
                        key={day}
                        className={`day-toggle-btn ${profile.availableDays.includes(day) ? 'active' : ''}`}
                        onClick={() => {
                          const newDays = profile.availableDays.includes(day)
                            ? profile.availableDays.filter(d => d !== day)
                            : [...profile.availableDays, day];
                          setProfile({...profile, availableDays: newDays});
                        }}
                      >
                        {day}
                      </button>
                    ))}
                  </div>
                </div>

                <button 
                  className="premium-btn primary save-btn mt-6" 
                  onClick={handleSaveProfile}
                  disabled={loading}
                >
                  <Save size={18} />
                  {loading ? "Persisting to Cloud..." : "Save Clinical Profile"}
                </button>
              </div>
            </section>

            {/* Alert Preferences Card */}
            <section className="settings-card glass-card">
              <div className="card-header">
                <Bell size={20} className="icon-blue" />
                <h3>Alert Preferences</h3>
              </div>
              <div className="card-body toggle-list">
                <div className="toggle-item">
                  <div className="toggle-text">
                    <span className="label">Critical Alerts</span>
                    <span className="desc">Immediate notification for danger status</span>
                  </div>
                  <label className="switch">
                    <input 
                      type="checkbox" 
                      checked={alerts.critical} 
                      onChange={() => toggleAlert('critical')}
                    />
                    <span className="slider round"></span>
                  </label>
                </div>

                <div className="toggle-item">
                  <div className="toggle-text">
                    <span className="label">Warning Alerts</span>
                    <span className="desc">Notify when patient vitals are abnormal</span>
                  </div>
                  <label className="switch">
                    <input 
                      type="checkbox" 
                      checked={alerts.warning} 
                      onChange={() => toggleAlert('warning')}
                    />
                    <span className="slider round"></span>
                  </label>
                </div>

                <div className="toggle-item">
                  <div className="toggle-text">
                    <span className="label">Device Offline Alerts</span>
                    <span className="desc">Alert when a patient's device goes offline</span>
                  </div>
                  <label className="switch">
                    <input 
                      type="checkbox" 
                      checked={alerts.offline} 
                      onChange={() => toggleAlert('offline')}
                    />
                    <span className="slider round"></span>
                  </label>
                </div>

                <div className="toggle-item">
                  <div className="toggle-text">
                    <span className="label">Daily Summary</span>
                    <span className="desc">Receive daily patient summary report</span>
                  </div>
                  <label className="switch">
                    <input 
                      type="checkbox" 
                      checked={alerts.dailySummary} 
                      onChange={() => toggleAlert('dailySummary')}
                    />
                    <span className="slider round"></span>
                  </label>
                </div>
              </div>
            </section>

            {/* Monitoring Card */}
            <section className="settings-card glass-card">
              <div className="card-header">
                <Activity size={20} className="icon-blue" />
                <h3>Monitoring</h3>
              </div>
              <div className="card-body">
                <div className="toggle-item large">
                  <div className="toggle-text">
                    <span className="label">Auto-refresh Dashboard</span>
                    <span className="desc">Refresh patient data automatically</span>
                  </div>
                  <label className="switch">
                    <input 
                      type="checkbox" 
                      checked={monitoring.autoRefresh} 
                      onChange={() => setMonitoring({...monitoring, autoRefresh: !monitoring.autoRefresh})}
                    />
                    <span className="slider round"></span>
                  </label>
                </div>
                
                <div className="interval-box">
                  <div className="interval-text">
                    <span className="label">Refresh Interval</span>
                    <span className="value">{monitoring.interval}</span>
                  </div>
                  <button className="action-btn-outline tiny">Change</button>
                </div>
              </div>
            </section>

            {/* Security Card */}
            <section className="settings-card glass-card">
              <div className="card-header">
                <Shield size={20} className="icon-blue" />
                <h3>Security</h3>
              </div>
              <div className="card-body">
                <div className="security-summary">
                  <Lock size={24} className="icon-blue mb-2" />
                  <p>Protect your medical credentials and system access.</p>
                </div>
                <button 
                  className="premium-btn primary update-btn w-full mt-4" 
                  onClick={() => setShowSecurityModal(true)}
                >
                  Change Password
                </button>
              </div>
            </section>
          </div>
        </div>
      </main>

      <SecurityModal 
        isOpen={showSecurityModal} 
        onClose={() => setShowSecurityModal(false)} 
      />
    </div>
  );
};

export default DoctorSettings;
